/* eslint-disable @typescript-eslint/no-unused-vars */
import useMedia from '@/hooks/useMedia'
import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useRef } from 'react'

// Lib
import { ProductInfoType } from '@/api/fetchProductData'
import { useObserve } from '@/hooks/useObserver'
import getDisplayPrice from '@/methods/getDisplayPrice'
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'

// Components
import Tags from '@/components/shared/Tags'
import VisibilityMask from '../LiveItem/VisiblityMask'
import Carousell from './components/Carousell'
import DiscountRateTag from './components/DiscountRateTag'

// Constants
import { getCurrencySymbol } from '@/constants/currency'
import { PRODUCT_STATUS } from '@/constants/livemallclub/productStatus'
import { ROUTE_PATH } from '@/constants/routePath'
import { ProductListProps } from '../LivePreview/ProductList'

// Methods

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

export interface ITagItem {
  style: number
  name: string
}

type IProductCard = {
  className?: string
  tags: ITagItem[]
  handleClick?: () => void
  isPromo?: boolean
  isMiniWidth?: boolean
  observer?: IntersectionObserver
  showSaleQuantity?: boolean
  inlineStyle?: any
  useFrom?: string
  isSearchPage?: boolean
  bgColor?: ProductListProps['bgColor']
  discountTagLayout?: ProductListProps['discountTagLayout']
  showVisibilityMask?: boolean
  isLive?: boolean
} & ProductInfoType

const ProductCard = (props: IProductCard) => {
  const {
    images,
    minLivePrice,
    minPrice,
    minOrigPrice,
    maxOrigPrice,
    productId,
    productName,
    tags,
    className,
    isPromo = false,
    isMiniWidth = false,
    observer,
    inlineStyle,
    currency,
    handleClick,
    isSearchPage = false,
    bgColor = 'white',
    discountTagLayout = 'block',
    showVisibilityMask = false,
    isLive,
  } = props

  const router = useRouter()
  const { isMobile } = useMedia()

  const { displayPrice, productPrice, productOriginalPrice } = getDisplayPrice({
    isLive,
    origPrice: maxOrigPrice,
    minPrice,
    livePrice: minLivePrice,
  })

  const productCardRef = useRef(null)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  useObserve({ observer: observer, targetRef: productCardRef.current })

  const slicedImages = images?.slice(0, 5)

  const handleGoToProductDetail = () => {
    handleRoutePushWithQuery({ router, pathname: `${ROUTE_PATH.PRODUCT}/${productId}` })
  }

  return (
    <div
      ref={productCardRef}
      data-selector-id='product-card'
      onClick={() => {
        if (showVisibilityMask) {
          return
        }

        handleGoToProductDetail()
        typeof handleClick === 'function' && handleClick()
      }}
      className={cx('productCard', className, { isSearchPage }, `bg-${bgColor}`)}
      style={inlineStyle}
      data-id={productId}
    >
      <a className={cx('productCard-link')} href={`${ROUTE_PATH.PRODUCT}/${productId}`} />

      <div className={cx('productCard__image', { hasNoImage: slicedImages?.length === 0 })}>
        <Carousell
          images={slicedImages}
          tags={tags}
          handleClick={() => {
            handleGoToProductDetail()
            typeof handleClick === 'function' && handleClick()
          }}
        />
      </div>
      <div className={cx('productCard__info', { isSearchPage })}>
        <div className={cx('productCard__info-header')}>
          {/* product name */}
          <div className={cx('productCard__name', { isSearchPage })}>{productName}</div>
        </div>

        {/* footer */}
        <div className={cx('productCard__info-footer', { isSearchPage })}>
          {/* discount */}
          <div className={cx('productCard__discount')}>
            {isMobile && isMiniWidth && discountTagLayout === 'block' && (
              <DiscountRateTag productPrice={productPrice} productOriginalPrice={productOriginalPrice} />
            )}
          </div>

          <div className={cx('productCard__discount', { hasDiscountTag: discountTagLayout === 'inline' })}>
            <div className={cx('productCard__discount-price')}>{`${getCurrencySymbol(currency)}${displayPrice}`}</div>
            {discountTagLayout === 'inline' && (
              <DiscountRateTag productPrice={productPrice} productOriginalPrice={productOriginalPrice} gapPosition='left' />
            )}
          </div>
          {/* tags */}
          {!isPromo && (
            <div className={cx('productCard__tags')}>
              <Tags tags={tags} max={3} />
            </div>
          )}
        </div>
      </div>
      {showVisibilityMask && <VisibilityMask initialVisibility={props.status === PRODUCT_STATUS.ON_SHELF} id={productId} type='product' />}
    </div>
  )
}

export default ProductCard
